import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { BizEntityFunctionTypes, BizEntityTypeCategory, IBizEntity, LocalStorageKeys } from '@optima-stride-ai/core';
import { GridLayout, IGridConfig, ToastService } from '@optima-stride-ai/optima-components';
import { BehaviorSubject } from 'rxjs';
import { ContentDataType, ContentType, iContentDimensions, iDashboard, iDashboardItem, iLayoutList } from '@optima-stride-ai/optima-app-framework';
import { DashboardStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { PinStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { AppStateService } from '../../../../services/state-store/app-state';
import { faker } from '@faker-js/faker';
import { iPinnedItem, PinItemContainerType, PinItemLocations, PinnedItemType } from '@optima-stride-ai/optima-app-framework';
import { BizEntityStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { BizEntityTypeStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { LocalStorageService } from '@optima-stride-ai/optima-platform';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private appStateService = inject(AppStateService);
  private toastService = inject(ToastService);
  private localStorageService: LocalStorageService = inject(LocalStorageService);
  private _cols = 6;
  public mainChartRef: WritableSignal<any[]> = signal([]);
  private readonly dashboardStateStoreService = inject(DashboardStateStoreService);
  private readonly pinStateStoreService : PinStateStoreService = inject(PinStateStoreService);
  _dashboardItems: iDashboardItem[] = [];
  private _dashboardListSubject = new BehaviorSubject<iDashboard[]>([]);
  dashboardList$ = this._dashboardListSubject.asObservable();
  _dashboardList: iDashboard[] = [];

  set col(cols: number) {
    this._cols = cols;
  }

  get col() { 
    return this._cols;
  }

  set dashboardList(dashList: iDashboard[]) {
    this._dashboardList = dashList;
    this._dashboardListSubject.next(dashList);
  }

  get dashboardList(): iDashboard[] {
    return this._dashboardList;
  }

  moveDashboardItem(sourceDashboard: iDashboard, targetDashboard: iDashboard, itemId: string , itemType : ContentDataType) {
    let sourceIndex = this._dashboardList.findIndex(obj => obj.id.toString() === sourceDashboard.id.toString() && obj.org_name === sourceDashboard.org_name);
    let targetIndex = this._dashboardList.findIndex(obj => obj.id.toString() === targetDashboard.id.toString() && obj.org_name === targetDashboard.org_name);
    let sourceLayoutList : iLayoutList | undefined =  this._dashboardList[sourceIndex].layoutList?.find(obj => obj.id.toString() === itemId.toString() && obj.type === itemType);
    
    if (sourceLayoutList) {
      let targetLayloutList = this._dashboardList[targetIndex]?.layoutList || [];

      if(targetLayloutList.findIndex(obj => obj.id.toString() === itemId.toString() && obj.type === itemType) >= 0){
        this.toastService.toast(`This item alteardy exists in ${targetDashboard.label}`);
      }else{


        let sourceLayoutListItemIndex = (this._dashboardList[sourceIndex].layoutList || []).findIndex(obj => obj.id.toString() === itemId.toString() && obj.type === itemType);

        
        if(sourceLayoutListItemIndex>=0){
          this._dashboardList[sourceIndex].layoutList?.splice(sourceLayoutListItemIndex, 1);
          let maxY = 0;
          if(targetLayloutList.length > 0){
            maxY = Math.max(...(targetLayloutList).map(item => item.y)) || 0; 
          }
          sourceLayoutList.x = 0;
          sourceLayoutList.y = maxY + 1;
          this._dashboardList[targetIndex].layoutList?.push(sourceLayoutList);
        }
        else{
          this.toastService.toast(`Item not found in ${sourceDashboard.label}`);
        }
        this.saveDashboardListInLocalStorage();
      }
    }else{
      this.toastService.toast(`Item not found in ${sourceDashboard.label}`);
    }
    
  }

  _activeDashoardTabId: string | undefined;
  set activeDashoardTabId(tabId: string | undefined){
    this._activeDashoardTabId = tabId;
  }
  get activeDashoardTabId(){
    return this._activeDashoardTabId;
  }

  getActiveDashboard(org_name : string , activeDashboard : string): iDashboard {
    return this._dashboardList.find(obj => obj.id.toString() === activeDashboard) || {} as iDashboard;
  }

  addDashboard(dashboard: iDashboard) {
    dashboard.id = faker.string.uuid();
    dashboard.isAdHocTab = false;
    this._dashboardList.push(dashboard);
    this.saveDashboardListInLocalStorage();
  }

  removeDashboard(dashboard: iDashboard, dashboardList: iDashboard[]) {
    let index = dashboardList.findIndex(obj => obj.id === dashboard.id && obj.org_name === dashboard.org_name);
    dashboardList[index].pinAsTab = false;
    dashboardList[index].isAdHocTab = false;
    this.saveDashboardListInLocalStorage();
  }

  // pinDashboard(dashboard: iDashboard, dashboardList: iDashboard[]) {
  //   // let index = dashboardList.findIndex(obj => obj.id === dashboard.id && obj.org_name === dashboard.org_name);
  //   // dashboardList[index].pinAsTab = true;
  //   // this.saveDashboardListInLocalStorage();
  // }

  pinDashboardTab(dashboardId: string, pinAsTab: boolean, dashboardStateStoreService: DashboardStateStoreService) {
    let dashboard = this.dashboardList;
    let dashboardUpdate = dashboard.find((d) => d.id.toString() === dashboardId.toString());
    if (dashboardUpdate) {
      dashboardUpdate.pinAsTab = pinAsTab;
    }
    this.dashboardStateStoreService.update(dashboardUpdate!);
  }

  adHocOpenDashboard(dashboard: iDashboard, dashboardList: iDashboard[]) {
    dashboardList.forEach((obj) => {
      if (obj.org_name === dashboard.org_name){
        obj.isAdHocTab = false;
      }
    });

    if (!dashboard.pinAsTab) {
      let index = dashboardList.findIndex(obj => obj.id.toString() === dashboard.id && obj.org_name === dashboard.org_name);
      dashboardList[index].isAdHocTab = true;
    }

    this.saveDashboardListInLocalStorage();
  }

  updateDashboardLayout(dashboardId : string, layoutList : iLayoutList[], saveUpdatedLayout = false){
    let index = this._dashboardList.findIndex(obj => obj.id.toString() === dashboardId);
    // Ensure layoutList is always an array
    const safeLayoutList = Array.isArray(layoutList) ? layoutList : [];

    if(index >= 0){
        let CurrentLayoutList = this._dashboardList[index].layoutList || [];
        CurrentLayoutList.forEach(item1 => {
          const matchingItem = safeLayoutList.find((item2: { id: string; }) => item2.id.toString() === item1.id.toString());
          if (matchingItem) {
            item1.x = matchingItem.x;
            item1.y = matchingItem.y;
            item1.w = matchingItem.w;
            item1.h = matchingItem.h;
            // item1.maxH = matchingItem.maxH;
            // item1.maxW = matchingItem.maxW;
            item1.minW = 1;
            item1.minH = 2;
          }
        });
    }
    this.appStateService.updatedDashboardLayout = safeLayoutList;
    if(saveUpdatedLayout){
      this.updateDashboardList(this._dashboardList, this._dashboardList[index]);
      this.appStateService.updatedDashboardLayout = undefined;
    }
    // this.saveDashboardListInLocalStorage();
    // this.updateDashboardList(this._dashboardList, this._dashboardList[index]);

  }

  saveCurrentDashboardLayout(){
    let index = this._dashboardList.findIndex(obj => obj.id.toString() === this.appStateService.activeDashboardId);
    this.updateDashboardList(this._dashboardList, this._dashboardList[index]);
    this.appStateService.updatedDashboardLayout = undefined
  }

    addItemToDashboard(dashboard: iDashboard, entity: any, dimensions: iContentDimensions) {
    let localDashList = this._dashboardList;
    let index = localDashList.findIndex(obj => obj.id.toString() === dashboard.id.toString());
    const COLS = this.col; // Set your grid column count here

    if (index !== -1) {
      let layoutList = localDashList[index].layoutList || [];

      const newItemId = faker.string.uuid();
      const newItem = {
        id: newItemId,
        type: ContentDataType.BizEntity,
        x: 0,
        y: 0,
        w: dimensions?.w || 1,
        h: dimensions?.h || 3,
        minW: 1,
        minH: 2,
        maxW: 3,
        maxH: 5,
      };

      // Shift existing items horizontally
      const shiftedItems = layoutList.map(item => {
        let newX = item.x + newItem.w;
        let newY = item.y;

        // Wrap to next row if exceeds column limit
        if (newX + item.w > COLS) {
          newX = 0;
          newY += 1;
        }

        return { ...item, x: newX, y: newY };
      });

      // Add the new item at start
      shiftedItems.unshift(newItem);
      localDashList[index].layoutList = shiftedItems;

      // Build the dashboard UI item
      const dashboardItem: iDashboardItem = {
        id: newItemId,
        header: { 
          icon: entity?.icon || '', 
          title: entity?.name || entity?.title || 'Untitled', 
          action: [
            { 
              label: 'unPin', 
              icon: 'optimaEllipsisUnpin', 
              action: () => { }
            }
          ]
        },
        footer: {},
        content: { 
          type: ContentType.WIDGET, 
          data: { id: entity?.id || '', type: entity?.type || null } 
        }
      };

      if (!localDashList[index].items) {
        localDashList[index].items = [];
      }

      localDashList[index].items?.unshift(dashboardItem);
      this._dashboardItems.unshift(dashboardItem);

      this.toastService.toast(`${entity?.name || 'Item'} added to ${dashboard.label} dashboard`);
    }

    this.updateDashboardList(localDashList, dashboard);
}

  moveItemToDashboard(dashboard: iDashboard, entity: any, dimensions: iContentDimensions, entityContainerId: string, entityContainerItem: iLayoutList) {
      let localDashList = this._dashboardList;
      let index = localDashList.findIndex(obj => obj.id.toString() === dashboard.id.toString());
      const COLS = this.col; // Set your grid column count here
  
      if (index !== -1) {
        let layoutList = localDashList[index].layoutList || [];
  
        const newItemId = faker.string.uuid();
        const newItem = {
          id: newItemId,
          type: ContentDataType.BizEntity,
          x: 0,
          y: 0,
          w: entityContainerItem.w || dimensions?.w || 1,
          h: entityContainerItem.h || dimensions?.h || 2,
          minW: entityContainerItem.minW || 1,
          minH: entityContainerItem.minH || 2,
          maxW: entityContainerItem.maxW || 3,
          maxH: entityContainerItem.maxH || 5,
        };
  
        // Shift existing items horizontally
        const shiftedItems = layoutList.map(item => {
          let newX = item.x + newItem.w;
          let newY = item.y;
  
          // Wrap to next row if exceeds column limit
          if (newX + item.w > COLS) {
            newX = 0;
            newY += 1;
          }
  
          return { ...item, x: newX, y: newY };
        });
  
        // Add the new item at start
        shiftedItems.unshift(newItem);
        localDashList[index].layoutList = shiftedItems;
  
        // Build the dashboard UI item
        const dashboardItem: iDashboardItem = {
          id: newItemId,
          header: { 
            icon: entity?.icon || '', 
            title: entity?.name || entity?.title || 'Untitled', 
            action: [
              { 
                label: 'unPin', 
                icon: 'optimaEllipsisUnpin', 
                action: () => { }
              }
            ]
          },
          footer: {},
          content: { 
            type: ContentType.WIDGET, 
            data: { id: entity?.id || '', type: entity?.type || null } 
          }
        };
  
        if (!localDashList[index].items) {
          localDashList[index].items = [];
        }
  
        localDashList[index].items?.unshift(dashboardItem);
        this._dashboardItems.unshift(dashboardItem);
  
        this.toastService.toast(`${entity?.name || 'Item'} moved to ${dashboard.label} dashboard`);
      }
      this.removeDashboardItem(entityContainerId, this.activeDashoardTabId!);
      this.updateDashboardList(localDashList, dashboard);
  }

  removeDashboardItem(itemId:string, dashboardId:string) {
    let index = this._dashboardList.findIndex(obj => obj.id.toString() === dashboardId);
    if(index !== -1)
    {
      let layoutList = this._dashboardList[index].layoutList || [];
      let dashboardItemList = this._dashboardList[index].items || [];
      let itemIndex = layoutList.findIndex(obj => obj.id.toString() === itemId);
      let dashboardItemIndex = dashboardItemList.findIndex(obj => obj.id.toString() === itemId);

      if(itemIndex !== -1)
      {
        layoutList.splice(itemIndex, 1);
        if(dashboardItemIndex !== -1)
        {
          dashboardItemList.splice(dashboardItemIndex, 1);
        }
        this._dashboardList[index].layoutList = layoutList;
        this._dashboardList[index].items = dashboardItemList;
        this.updateDashboardLayout(dashboardId, layoutList, true);
        let dashboardName = this._dashboardList[index].label;
        // this.toastService.toast(`Item removed from ${dashboardName} dashboard`);
        // this.saveDashboardListInLocalStorage();
        this.updateDashboardList(this._dashboardList, this._dashboardList[index]);
      }
    }
  }

  saveDashboardListInLocalStorage(){
    this.localStorageService.save(LocalStorageKeys.dashboards, JSON.stringify(this._dashboardList));
  }

  updateDashboardList(dashboardList: iDashboard[], dashboard: iDashboard) {
    // let itemContainer = dashboard.pinnedItemContainers;
    let itemContainer = this.dashboardList.find(d => d.id === dashboard.id)?.pinnedItemContainers;
    let updatedPinnedItems: iPinnedItem<iDashboardItem>[] = [];
    
    dashboard?.layoutList?.forEach((item) => {
      let itemData = dashboard.items?.find(obj => obj.id === item.id);
      itemData = {
        ...itemData!,
        content: {
          type: ContentType.WIDGET,
          data:{
            id:itemData?.content?.data?.id,
            type:itemData?.content?.data?.type
          }
        }
      }
      if(itemData)
      {
        let pinnedItem: iPinnedItem<iDashboardItem> = {
          id: item.id,
          type: PinnedItemType.DashboardItem,
          spatiality: {
            left: item.x.toString(),
            top: item.y.toString(),
            width: item.w.toString(),
            height: item.h.toString()
          },
          data: itemData
        }
        updatedPinnedItems.push(pinnedItem);
      }
    });
    
    dashboard.pinnedItemContainers = {
      id: itemContainer?.id || faker.number.int().toString(),
      uuid: itemContainer?.uuid || faker.string.uuid(),
      type: itemContainer?.type || PinItemContainerType.Dashboard,
      location: itemContainer?.location || PinItemLocations.Dashboard,
      locationId: itemContainer?.locationId || dashboard.id,
      pinnedItems: updatedPinnedItems,
      ownerId: '19'
    }

    if(itemContainer){

      this.pinStateStoreService.update(dashboard.pinnedItemContainers)
    }else{

      this.pinStateStoreService.create(dashboard.pinnedItemContainers)
    }
  }

  
sortEntitiesByTypeAndPopulateData(grid: IGridConfig<IBizEntity>,
    benBizEntityStateStoreService: BizEntityStateStoreService,
    bizEntityTypesStateStoresService: BizEntityTypeStateStoreService
  ): Promise<void> {
    // Guard clause to check if grid and grid.layout exist
    if (!grid || !grid.layout) {
      return Promise.resolve();
    }

let localCardData: GridLayout<IBizEntity> = grid.layout
let bizEntityIds: string[] = [];
for (const card of localCardData) {
  if(!card.data) continue;

 bizEntityIds.push(card.data.id);
}

return this.fetchDataForRespectiveIds(grid, bizEntityIds, benBizEntityStateStoreService, bizEntityTypesStateStoresService);
  }

  fetchDataForRespectiveIds(
    grid:IGridConfig<IBizEntity>,
    bizEntityIds: string[],
    benBizEntityStateStoreService: BizEntityStateStoreService,
    bizEntityTypesStateStoresService: BizEntityTypeStateStoreService,
  ): Promise<void> {
    let bizEntities: IBizEntity[] = [];

    const promises: Promise<void>[] = [];

    if (bizEntityIds.length > 0) {
      promises.push(
        benBizEntityStateStoreService
          .getState(bizEntityTypesStateStoresService)
          .then((data) => {
            bizEntities = data || [];
          })
      );
    }


    return Promise.all(promises)
      .then(() => {
        this.populateCardDataWithFetchedData(bizEntities, grid);
      })
      .catch((err) => {
      });
  }

  populateCardDataWithFetchedData(bizEntities: IBizEntity[], grid:IGridConfig<any>) {
    let localCardData: GridLayout<any> = grid.layout
    localCardData.forEach((card) => {
      if(!card.data) return;
      
      if (card.data?.type.functionType === BizEntityFunctionTypes.Structural) {
        const bizEntity = bizEntities.find((entity) => entity.id === card.data?.id);
        if (bizEntity) {
          const { parentPath, ...rest } = bizEntity;
          card.data = rest;
        }
      } else {
          const bizEntity = bizEntities.find((entity) => entity.id === card.data?.id);
          if (bizEntity) {
            const { parentPath, ...rest } = bizEntity;
            card.data = rest;
          }
      }
    });
    localCardData = [...localCardData];
    grid.layout = localCardData;
  }
}

